<template>
  <div
    class="sf-banner"
    :class="[
      outerContainer,
      { 'sf-banner-inline--has-actions': !!$slots.actions },
    ]"
    v-bind="$attrs"
    @click="handleClick"
  >
    <span
      v-if="showIcon"
      class="sf-banner-icon fa tw-leading-[18px]"
      :class="effectiveIconClass"
    />
    <span class="sf-banner-text">
      <span class="sf-banner-title tw-leading-[18px]">
        <slot name="title">{{ title }}</slot>
      </span>
      <span
        v-if="description || $slots.description"
        class="sf-banner-description tw-leading-[18px]"
      >
        <slot name="description">{{ description }}</slot>
      </span>
    </span>
    <div v-if="$slots.actions" class="sf-banner-right">
      <div class="sf-banner-actions">
        <slot name="actions" />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { FlexAlign } from '@app/shared/constants'
import type { FlexAlignType } from '@app/shared/constants'

import { bannerVariants } from '@app/shared/types/banner'
import type { BannerVariant } from '@app/shared/types/banner'

import { computed, getCurrentInstance } from 'vue'

import type { BannerIconType } from './constants'
import { icons } from './constants'

defineOptions({
  name: 'SfBannerInline',
})

const instance = getCurrentInstance()

const emit = defineEmits(['click'])

interface Props {
  align?: Extract<FlexAlignType, 'start' | 'center'>
  textAlign?: Extract<FlexAlignType, 'start' | 'center'>
  iconClass?: BannerIconType
  title?: string
  description?: string
  variant: BannerVariant
  showIcon?: boolean
  hoverable?: boolean
  isFullWidth?: boolean
  hasLongText?: boolean
  singleLine?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  align: FlexAlign.CENTER,
  textAlign: FlexAlign.START,
  iconClass: '',
  title: '',
  description: '',
  variant: bannerVariants.info,
  showIcon: true,
  hoverable: false,
  isFullWidth: false,
  hasLongText: false,
  singleLine: false,
})

const effectiveIconClass = computed(() => {
  if (props.iconClass) {
    return props.iconClass
  }

  return icons[props.variant]
})

// Slot presence is read straight from $slots in the template: a computed
// over $slots has no reactive dependency, so it would cache the state of
// the first render and never see a caller's conditional slot appear.
const outerContainer = computed(() => {
  const effectiveClass = `sf-banner-inline-${props.variant}`
  return {
    [effectiveClass]: true,
    [`${effectiveClass}--hoverable`]: props.hoverable,
    [`${effectiveClass}--full-width`]: props.isFullWidth,
    'sf-banner-inline--single-line': props.singleLine,
    'sf-banner-long-text': props.hasLongText,
    'tw-items-center': props.align === FlexAlign.CENTER,
    'tw-items-start': props.align === FlexAlign.START,
    'tw-justify-center': props.textAlign === FlexAlign.CENTER,
    'tw-justify-start': props.textAlign === FlexAlign.START,
  }
})

// A banner nobody listens to must let the click through: document-level
// outside-click handlers (dropdowns, tooltips) fire in the bubble phase, so
// stopping propagation here would leave them open.
function handleClick(e: Event): void {
  if (!instance?.vnode.props?.onClick) return

  e.stopPropagation()
  e.preventDefault()
  emit('click', e)
}
</script>
